import {
    type AppliedDiscount,
    type CartItem,
    PERCENTAGE_BASED_DISCOUNTS,
} from './types';

export function calculateDiscountAmount(
    discount: AppliedDiscount,
    subTotal: number,
    total = 0,
) {
    const rewardAmount = discount.rewardAmount ?? 0;

    const isPercentageDiscount = PERCENTAGE_BASED_DISCOUNTS.includes(
        discount.rewardType,
    );

    if (isPercentageDiscount) {
        return (rewardAmount / 100) * subTotal;
    }

    return (subTotal / (total || subTotal)) * rewardAmount;
}

export function calculateDiscounts(
    appliedDiscounts: AppliedDiscount[],
    cartItems: CartItem[],
    subtotal: number,
) {
    const itemSubTotals = new Map(
        cartItems.map((item) => [item.id, item.subTotal]),
    );
    const computedDiscounts: AppliedDiscount[] = [];
    const discountsByItem = new Map<string, AppliedDiscount[]>();
    const cartItemIds = cartItems.map((cartItem) => cartItem.id);
    let runningTotal = subtotal;

    for (const discount of appliedDiscounts) {
        let discountAmount = 0;

        const isPercentageDiscount = PERCENTAGE_BASED_DISCOUNTS.includes(
            discount.rewardType,
        );

        const affectedItemIds: string[] = [];

        if (discount.scope === 'cart') {
            affectedItemIds.push(
                ...cartItemIds.filter(
                    (cartItem) => !discount.itemIds?.includes(cartItem),
                ),
            );
        }

        if (discount.scope === 'item') {
            affectedItemIds.push(...Array.from(discount.itemIds));
        }

        for (const [index, itemId] of affectedItemIds.entries()) {
            const itemSubTotal = itemSubTotals.get(itemId) ?? 0;

            let itemDiscount = 0;

            if (isPercentageDiscount) {
                itemDiscount = calculateDiscountAmount(
                    discount,
                    itemSubTotal,
                    runningTotal,
                );
            } else {
                itemDiscount =
                    index === affectedItemIds.length - 1
                        ? discount.rewardAmount - discountAmount
                        : Math.floor(
                              calculateDiscountAmount(
                                  discount,
                                  itemSubTotal,
                                  runningTotal,
                              ),
                          );
            }

            itemSubTotals.set(itemId, itemSubTotal - itemDiscount);

            discountsByItem.set(itemId, [
                ...(discountsByItem.get(itemId) ?? []),
                { ...discount, discountAmount: itemDiscount },
            ]);
            discountAmount += itemDiscount;
        }

        runningTotal -= discountAmount;

        computedDiscounts.push({
            ...discount,
            discountAmount,
        });
    }

    return {
        totalAfterDiscount: runningTotal,
        computedDiscounts,
        discountsByItem,
        itemSubTotals,
    };
}

export function prepareDiscountPayload(discount: AppliedDiscount) {
    return {
        discountValue: discount.rewardAmount,
        discountId: discount.id,
        discountName: discount.name,
        discountAmount: discount.discountAmount,
        rewardType: discount.rewardType,
        scope: discount.scope,
    };
}

export function pruneInvalidDiscounts(
    discounts: AppliedDiscount[],
    cartItems: CartItem[],
): AppliedDiscount[] {
    const cartItemIds = new Set(cartItems.map((cartItem) => cartItem.id));

    return discounts.reduce<AppliedDiscount[]>((validDiscounts, discount) => {
        if (discount.scope === 'cart') {
            if (cartItemIds.size === 0) return validDiscounts;

            validDiscounts.push(discount);
            return validDiscounts;
        }

        const itemIds = discount.itemIds.filter((itemId) =>
            cartItemIds.has(itemId),
        );

        if (itemIds.length === 0) return validDiscounts;

        validDiscounts.push({ ...discount, itemIds });
        return validDiscounts;
    }, []);
}

export function sortDiscounts(discounts: AppliedDiscount[]): AppliedDiscount[] {
    return [...discounts].sort((a, b) => {
        if (a.rewardType !== b.rewardType)
            return PERCENTAGE_BASED_DISCOUNTS.includes(b.rewardType) ? 1 : -1;

        return b.rewardAmount - a.rewardAmount;
    });
}
